/**
 * Tabelas oficiais de códigos fiscais usadas nos cadastros de grupo tributário
 * e categoria fiscal.
 *
 * O `value` é sempre string porque as colunas correspondentes no banco são
 * varchar e preservam o zero à esquerda ("00", "01", "0").
 */

export type FiscalOption = {
  value: string;
  label: string;
};

export type FiscalOptionGroup = {
  label: string;
  options: FiscalOption[];
};

/**
 * Tabela A - Origem da mercadoria (coluna `origem`, varchar(1), 0 a 8).
 */
export const ORIGEM_MERCADORIA_OPTIONS: FiscalOption[] = [
  {
    value: '0',
    label: '0 - Nacional, exceto as indicadas nos códigos 3, 4, 5 e 8',
  },
  {
    value: '1',
    label: '1 - Estrangeira - Importação direta, exceto a indicada no código 6',
  },
  {
    value: '2',
    label:
      '2 - Estrangeira - Adquirida no mercado interno, exceto a indicada no código 7',
  },
  {
    value: '3',
    label: '3 - Nacional, com conteúdo de importação superior a 40% e até 70%',
  },
  {
    value: '4',
    label: '4 - Nacional, produção conforme processos produtivos básicos',
  },
  {
    value: '5',
    label: '5 - Nacional, com conteúdo de importação de até 40%',
  },
  {
    value: '6',
    label:
      '6 - Estrangeira - Importação direta, sem similar nacional (lista CAMEX)',
  },
  {
    value: '7',
    label:
      '7 - Estrangeira - Adquirida no mercado interno, sem similar nacional (lista CAMEX)',
  },
  {
    value: '8',
    label: '8 - Nacional, com conteúdo de importação superior a 70%',
  },
];

/**
 * CSOSN - Código de Situação da Operação no Simples Nacional.
 * Usado no campo `cst_csosn` quando a empresa é do Simples Nacional.
 */
export const CSOSN_OPTIONS: FiscalOption[] = [
  { value: '101', label: '101 - Tributada com permissão de crédito' },
  { value: '102', label: '102 - Tributada sem permissão de crédito' },
  {
    value: '103',
    label: '103 - Isenção do ICMS para faixa de receita bruta',
  },
  {
    value: '201',
    label: '201 - Tributada com permissão de crédito e com ICMS por ST',
  },
  {
    value: '202',
    label: '202 - Tributada sem permissão de crédito e com ICMS por ST',
  },
  {
    value: '203',
    label: '203 - Isenção para faixa de receita bruta e com ICMS por ST',
  },
  { value: '300', label: '300 - Imune' },
  { value: '400', label: '400 - Não tributada pelo Simples Nacional' },
  {
    value: '500',
    label: '500 - ICMS cobrado anteriormente por ST ou por antecipação',
  },
  { value: '900', label: '900 - Outros' },
];

/**
 * Tabela B - CST do ICMS. Usado no campo `cst_csosn` quando a empresa é do
 * regime normal (Lucro Real / Lucro Presumido).
 */
export const CST_ICMS_OPTIONS: FiscalOption[] = [
  { value: '00', label: '00 - Tributada integralmente' },
  { value: '10', label: '10 - Tributada e com cobrança do ICMS por ST' },
  { value: '20', label: '20 - Com redução de base de cálculo' },
  {
    value: '30',
    label: '30 - Isenta ou não tributada e com cobrança do ICMS por ST',
  },
  { value: '40', label: '40 - Isenta' },
  { value: '41', label: '41 - Não tributada' },
  { value: '50', label: '50 - Suspensão' },
  { value: '51', label: '51 - Diferimento' },
  {
    value: '60',
    label: '60 - ICMS cobrado anteriormente por substituição tributária',
  },
  {
    value: '70',
    label: '70 - Com redução de base de cálculo e cobrança do ICMS por ST',
  },
  { value: '90', label: '90 - Outras' },
];

/**
 * O campo `cst_csosn` guarda CSOSN ou CST do ICMS conforme o regime da empresa.
 * Para localizar um código já gravado é preciso procurar nas duas tabelas, já
 * que a coluna não distingue uma da outra.
 */
export const CST_CSOSN_TODOS_OPTIONS: FiscalOption[] = [
  ...CSOSN_OPTIONS,
  ...CST_ICMS_OPTIONS,
];

/**
 * Opções do campo `cst_csosn` para o regime da empresa: CSOSN no Simples
 * Nacional, CST do ICMS no regime normal.
 *
 * Com o regime desconhecido (empresa sem regime cadastrado ou falha ao
 * carregá-lo) devolve as duas tabelas agrupadas, em vez de arriscar esconder o
 * código que o usuário precisa.
 */
export function getCstCsosnOptions(
  regime: 'simples_nacional' | 'lucro_presumido' | 'lucro_real' | null,
): FiscalOption[] | FiscalOptionGroup[] {
  if (regime === 'simples_nacional') {
    return CSOSN_OPTIONS;
  }

  if (regime === 'lucro_presumido' || regime === 'lucro_real') {
    return CST_ICMS_OPTIONS;
  }

  return [
    { label: 'CSOSN - Simples Nacional', options: CSOSN_OPTIONS },
    { label: 'CST ICMS - Regime Normal', options: CST_ICMS_OPTIONS },
  ];
}

/**
 * CST de PIS e COFINS (tabelas 4.3.3 e 4.3.4), restrito às situações de saída,
 * que são as que fazem sentido num grupo tributário de venda.
 */
export const CST_PIS_COFINS_OPTIONS: FiscalOption[] = [
  { value: '01', label: '01 - Operação tributável com alíquota básica' },
  { value: '02', label: '02 - Operação tributável com alíquota diferenciada' },
  {
    value: '03',
    label: '03 - Operação tributável com alíquota por unidade de medida',
  },
  {
    value: '04',
    label: '04 - Operação tributável monofásica - revenda a alíquota zero',
  },
  {
    value: '05',
    label: '05 - Operação tributável por substituição tributária',
  },
  { value: '06', label: '06 - Operação tributável a alíquota zero' },
  { value: '07', label: '07 - Operação isenta da contribuição' },
  { value: '08', label: '08 - Operação sem incidência da contribuição' },
  { value: '09', label: '09 - Operação com suspensão da contribuição' },
  { value: '49', label: '49 - Outras operações de saída' },
  { value: '99', label: '99 - Outras operações' },
];

/**
 * CFOP de saída dentro do estado (5xxx) - os usados em NFC-e.
 * O campo `cfop_interno` é validado no backend por /^5\d{3}$/.
 */
export const CFOP_INTERNO_OPTIONS: FiscalOption[] = [
  { value: '5101', label: '5101 - Venda de produção do estabelecimento' },
  {
    value: '5102',
    label: '5102 - Venda de mercadoria adquirida ou recebida de terceiros',
  },
  {
    value: '5103',
    label:
      '5103 - Venda de produção do estabelecimento, fora do estabelecimento',
  },
  {
    value: '5104',
    label: '5104 - Venda de mercadoria de terceiros, fora do estabelecimento',
  },
  {
    value: '5401',
    label: '5401 - Venda de produção do estabelecimento, ST como substituto',
  },
  {
    value: '5403',
    label: '5403 - Venda de mercadoria de terceiros, ST como substituto',
  },
  {
    value: '5405',
    label: '5405 - Venda de mercadoria de terceiros, ST como substituído',
  },
  {
    value: '5656',
    label:
      '5656 - Venda de combustível ou lubrificante de terceiros a consumidor final',
  },
  { value: '5910', label: '5910 - Remessa em bonificação, doação ou brinde' },
  {
    value: '5929',
    label: '5929 - Lançamento relativo a operação registrada em ECF',
  },
];

/**
 * CFOP de saída interestadual (6xxx). Guardado apenas como dado cadastral -
 * não é usado na emissão da NFC-e, que é sempre interna.
 */
export const CFOP_INTERESTADUAL_OPTIONS: FiscalOption[] = [
  { value: '6101', label: '6101 - Venda de produção do estabelecimento' },
  {
    value: '6102',
    label: '6102 - Venda de mercadoria adquirida ou recebida de terceiros',
  },
  {
    value: '6107',
    label: '6107 - Venda de produção do estabelecimento a não contribuinte',
  },
  {
    value: '6108',
    label: '6108 - Venda de mercadoria de terceiros a não contribuinte',
  },
  {
    value: '6401',
    label: '6401 - Venda de produção do estabelecimento, ST como substituto',
  },
  {
    value: '6403',
    label: '6403 - Venda de mercadoria de terceiros, ST como substituto',
  },
  {
    value: '6404',
    label: '6404 - Venda de mercadoria com ST retida anteriormente',
  },
  { value: '6910', label: '6910 - Remessa em bonificação, doação ou brinde' },
];

/**
 * Encontra a opção correspondente a um código já gravado, para popular o select
 * nas telas de edição.
 *
 * Códigos gravados antes destes selects existirem podem estar fora da tabela
 * (o campo era texto livre). Nesse caso devolvemos uma opção sintética marcada
 * como fora da tabela, em vez de `null`: assim o valor continua visível e não é
 * apagado por um salvamento que apenas passou pela tela.
 */
export function findFiscalOption(
  options: FiscalOption[],
  value?: string | null,
): FiscalOption | null {
  if (!value) {
    return null;
  }

  const known = options.find((option) => option.value === value);
  if (known) {
    return known;
  }

  return { value, label: `${value} - código fora da tabela` };
}
